// M6 — concrete TraceSource. `enrich` reuses fetchEnrichment from the
// agent-ledger read split (credential+workspaceId+window injected at the edge);
// `recoverConversationId` reads gen_ai.conversation.id for one responseId and
// only trusts a validated conv_ id (DESIGN §6 — a stray id must not mis-attribute
// an a2a turn). Both degrade to empty/null on a failed LA query (DESIGN §9:
// enrichment-degrade is acceptable; the pipeline counts a transcript miss, not
// this). The KQL I/O itself lives in @m8t-stack/agent-ledger/read; here we only
// orchestrate + validate, so unit tests inject pure querier fns.
import type { RoundEnrichment } from "@m8t-stack/agent-ledger/read";
import type { TraceSource } from "./types";

const CONV_ID = /^conv_[A-Za-z0-9]+$/;

export interface TraceQueriers {
  /** respIds + window → enrichment map (entry point binds fetchEnrichment.bind(credential, workspaceId)). */
  enrichFn: (respIds: string[], from: string, to: string) => Promise<Map<string, RoundEnrichment>>;
  /** one respId + window → its gen_ai.conversation.id (or null if no/aged-out span). */
  convIdFn: (respId: string, from: string, to: string) => Promise<string | null>;
}

export function makeTraceSource(q: TraceQueriers): TraceSource {
  return {
    async enrich(respIds: string[], window: { from: string; to: string }): Promise<Map<string, RoundEnrichment>> {
      try {
        return await q.enrichFn(respIds, window.from, window.to);
      } catch {
        // Enrichment degrades to ledger-only (DESIGN §9) — never fatal.
        return new Map();
      }
    },
    async recoverConversationId(respId: string, window: { from: string; to: string }): Promise<string | null> {
      let id: string | null;
      try {
        id = await q.convIdFn(respId, window.from, window.to);
      } catch {
        return null; // a failed recovery query degrades to "not recoverable"
      }
      return id != null && CONV_ID.test(id) ? id : null;
    },
  };
}
